/**
 * English or Spanish for the console (V6 design: Settings > Channels > Language). English
 * is the default. Picking Spanish loads the design's dictionary and runs its pass over the
 * whole page (domPass.ts), including drawers and dialogs, and keeps it running while pages
 * change; picking English puts every word back.
 *
 * Where the pick is kept: on the account (PATCH /auth/profile {lang}; /auth/me reads it back
 * on any device) and in this browser per person (localStorage, wrapped: a private window
 * still works, for that tab only). While viewing as another role it is kept in this
 * browser only: the server refuses writes then.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { asLang, loadSpanish, profileLangSave, spanish, type EsEngine, type Lang } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { esPass, watchEs, type EsStore } from './domPass';

const DEVICE_KEY = 'aithora.console.lang';
const userKey = (id: string) => `${DEVICE_KEY}.${id}`;

function readLocal(userId: string | null): Lang | null {
  try {
    const v = window.localStorage.getItem(userId ? userKey(userId) : DEVICE_KEY);
    return v === 'es' || v === 'en' ? v : null;
  } catch {
    return null;
  }
}

function writeLocal(userId: string | null, l: Lang): void {
  try {
    window.localStorage.setItem(DEVICE_KEY, l);
    if (userId) window.localStorage.setItem(userKey(userId), l);
  } catch {
    /* storage off: this tab only */
  }
}

/** Where the last pick was kept: on the account, or in this browser only. */
export type LangSaved = 'account' | 'device' | null;

interface LangValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  saved: LangSaved;
  /** The Spanish for one line (the line itself in English, or when there is none). */
  t: (s: string) => string;
}

const Ctx = createContext<LangValue>({ lang: 'en', setLang: () => undefined, saved: null, t: (s) => s });

export function useLang(): LangValue {
  return useContext(Ctx);
}

export function LangProvider({ children }: { children: ReactNode }) {
  const { session, client, viewAs } = useSession();
  const userId = session?.user.id ?? null;
  const serverLang = session?.user.lang ?? null;
  const [lang, setLangState] = useState<Lang>(() => readLocal(userId) ?? readLocal(null) ?? 'en');
  const [saved, setSaved] = useState<LangSaved>(null);
  const [engine, setEngine] = useState<EsEngine | null>(() => spanish());
  const store = useRef<EsStore>(new WeakMap());

  // The account's pick wins when the person signs in (or the session is checked); with
  // none on the account, this browser's pick for this person stands.
  useEffect(() => {
    if (serverLang) {
      setLangState(serverLang);
      writeLocal(userId, serverLang);
    } else if (userId) {
      const mine = readLocal(userId);
      if (mine) setLangState(mine);
    }
  }, [userId, serverLang]);

  useEffect(() => {
    try {
      document.documentElement.lang = lang;
    } catch {
      /* no document */
    }
    if (lang !== 'es') return;
    let live = true;
    let stop: (() => void) | null = null;
    void loadSpanish().then((A) => {
      if (!live) return;
      setEngine(A);
      esPass(document.body, true, store.current, A);
      stop = watchEs(document.body, store.current, A);
    });
    return () => {
      live = false;
      stop?.();
      // Back to English: every swapped word is put back.
      esPass(document.body, false, store.current, null);
    };
  }, [lang]);

  const setLang = useCallback(
    (l: Lang) => {
      const next = asLang(l);
      setLangState(next);
      writeLocal(userId, next);
      if (!session || viewAs) {
        setSaved('device');
        return;
      }
      profileLangSave(client, { body: { lang: next } }).then(
        () => setSaved('account'),
        () => setSaved('device'),
      );
    },
    [client, session, userId, viewAs],
  );

  const t = useCallback((s: string) => (lang === 'es' && engine ? engine.tr(s) ?? s : s), [lang, engine]);
  const value = useMemo(() => ({ lang, setLang, saved, t }), [lang, setLang, saved, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
